import { useEffect, useState } from "react";
import { Plus, Minus, Package, AlertTriangle, Trash2, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import ImportCsv from "../components/ImportCsv";
import Modal from "../components/Modal";
import { inputCls, labelCls } from "../utils/datetime";

const EMPTY = { item: "", category: "Consumable", stock: "", reorder_level: "5", unit: "pcs", item_code: "", batch_code: "", purchase_price: "", expiry_date: "" };

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [query, setQuery] = useState("");
  const [txnItem, setTxnItem] = useState(null);
  const [txnForm, setTxnForm] = useState({ type: "in", qty: "1", notes: "" });
  const [txnBusy, setTxnBusy] = useState(false);

  const submitTxn = async (e) => {
    e.preventDefault();
    setTxnBusy(true);
    try {
      const r = await api.post(`/connect/inventory/${txnItem.id}/txn`, { ...txnForm, qty: Number(txnForm.qty) || 0 });
      toast.success(`Stock updated — ${txnItem.item}: ${r.data.new_stock} ${txnItem.unit || "pcs"}`);
      setTxnItem(null);
      setTxnForm({ type: "in", qty: "1", notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update stock");
    }
    setTxnBusy(false);
  };

  const load = () => api.get("/connect/inventory").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/connect/list/vendors").then((r) => setVendors(r.data || [])).catch(() => {}); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/connect/inventory", {
        ...form, vendor_id: vendorId,
        stock: Number(form.stock) || 0, reorder_level: Number(form.reorder_level) || 5,
        purchase_price: Number(form.purchase_price) || 0,
      });
      toast.success(`Item saved — code ${r.data.item_code}`);
      setForm(EMPTY);
      setVendorId("");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the item");
    }
  };

  const adjust = async (id, stock, delta) => {
    await api.patch(`/connect/inventory/${id}`, { stock: Math.max(0, stock + delta) });
    load();
  };

  const me = JSON.parse(localStorage.getItem("dyntra_connect_user") || "{}");
  const canDelete = ["super_admin", "owner", "admin", "clinic_manager"].includes(me.level || "");

  const remove = async (id) => {
    try {
      await api.delete(`/connect/inventory/${id}`);
      toast.success("Item removed");
    } catch {
      toast.error("Could not remove the item");
    }
    load();
  };

  return (
    <div data-testid="connect-inventory" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-pine-deep">Inventory</h2>
          <p className="text-sm text-slate-500">Track stock levels and reorder before you run out.</p>
        </div>
        <div className="flex gap-2">
          <ImportCsv module="inventory" onDone={load} testid="inventory-import-csv" />
          <button data-testid="inventory-add-toggle" onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-2 rounded-full bg-pine px-5 py-2.5 text-xs font-bold text-ivory shadow-lift hover:bg-pine-deep transition-all">
            <Plus className="h-4 w-4" /> Add Item
          </button>
        </div>
      </div>

      {showForm && (
        <form data-testid="inventory-add-form" onSubmit={add} className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input required data-testid="inventory-name" placeholder="Item name" value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} className="lg:col-span-2 h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
          <select data-testid="inventory-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-pine">
            {["Consumable", "Restorative", "Prosthetic", "Cosmetic", "Instruments", "Endodontic", "Dermatology"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <select data-testid="inventory-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-pine">
            {["pcs", "packets", "boxes", "tubes", "bottles", "strips", "units"].map((u) => <option key={u}>{u}</option>)}
          </select>
          <input required data-testid="inventory-stock" type="number" min="0" inputMode="numeric" placeholder="Stock quantity" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
          <input data-testid="inventory-reorder" type="number" min="0" inputMode="numeric" placeholder="Reorder at" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
          <input data-testid="inventory-item-code" placeholder="Item code (auto if blank)" value={form.item_code} onChange={(e) => setForm({ ...form, item_code: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
          <input data-testid="inventory-batch-code" placeholder="Batch / packet code" value={form.batch_code} onChange={(e) => setForm({ ...form, batch_code: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
          <select data-testid="inventory-vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-pine">
            <option value="">Vendor (optional)</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <input data-testid="inventory-price" type="number" min="0" inputMode="numeric" placeholder="Purchase price ₹" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
          <input data-testid="inventory-expiry" type="date" title="Expiry date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
          <button data-testid="inventory-submit" className="h-11 rounded-full bg-teal-brand text-white text-xs font-bold hover:bg-pine transition-colors">Save Item</button>
        </form>
      )}

      <div className="relative">
        <input data-testid="inventory-search" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search items — name, item code, batch code…"
          className="h-11 w-full rounded-xl border border-pine/15 bg-white pl-4 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
      </div>

      <div className="rounded-2xl bg-white border border-pine/8 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead>
              <tr className="border-b border-pine/8 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <th className="px-5 py-3.5 font-bold">Item</th>
                <th className="px-5 py-3.5 font-bold">Category</th>
                <th className="px-5 py-3.5 font-bold">Stock</th>
                <th className="px-5 py-3.5 font-bold">Reorder At</th>
                <th className="px-5 py-3.5 font-bold">Status</th>
                <th className="px-5 py-3.5 font-bold">Adjust</th>
              </tr>
            </thead>
            <tbody>
              {items.filter((i) => {
                const q = query.trim().toLowerCase();
                return !q || [i.item, i.item_code, i.batch_code, i.category].some((v) => String(v || "").toLowerCase().includes(q));
              }).map((i) => {
                const low = i.stock <= i.reorder_level;
                return (
                  <tr key={i.id} data-testid={`inventory-row-${i.id.slice(0, 8)}`} className="border-b border-pine/5 last:border-0 hover:bg-cream/50">
                    <td className="px-5 py-3.5 font-bold text-pine-deep">{i.item}
                      {i.item_code && <p className="text-[10px] font-bold text-gold">{i.item_code}{i.batch_code ? ` · Batch ${i.batch_code}` : ""}</p>}
                    </td>
                    <td className="px-5 py-3.5 text-slate-600">{i.category}</td>
                    <td className="px-5 py-3.5 font-bold text-pine-deep">{i.stock} <span className="text-xs font-normal text-slate-400">{i.unit}</span></td>
                    <td className="px-5 py-3.5 text-slate-600">{i.reorder_level}</td>
                    <td className="px-5 py-3.5">
                      {i.stock === 0 ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-500 px-2.5 py-1 text-[10px] font-bold"><AlertTriangle className="h-3 w-3" /> Out of Stock</span>
                      ) : low ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-600 px-2.5 py-1 text-[10px] font-bold"><AlertTriangle className="h-3 w-3" /> Low</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-teal-soft text-teal-brand px-2.5 py-1 text-[10px] font-bold"><Package className="h-3 w-3" /> In Stock</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex gap-1.5">
                        <button data-testid={`inventory-txn-${i.id.slice(0, 8)}`} onClick={() => { setTxnItem(i); setTxnForm({ type: "in", qty: "1", notes: "" }); }}
                          className="inline-flex h-7 items-center gap-1 rounded-full bg-teal-soft px-2.5 text-[10px] font-bold text-teal-brand hover:bg-teal-brand hover:text-white transition-colors" aria-label="Stock transaction">
                          <ArrowLeftRight className="h-3 w-3" /> Stock
                        </button>
                        <button data-testid={`inventory-minus-${i.id.slice(0, 8)}`} onClick={() => adjust(i.id, i.stock, -1)} className="h-7 w-7 rounded-full border border-pine/15 flex items-center justify-center text-pine hover:bg-cream" aria-label="Decrease stock"><Minus className="h-3.5 w-3.5" /></button>
                        <button data-testid={`inventory-plus-${i.id.slice(0, 8)}`} onClick={() => adjust(i.id, i.stock, 1)} className="h-7 w-7 rounded-full bg-pine flex items-center justify-center text-ivory hover:bg-pine-deep" aria-label="Increase stock"><Plus className="h-3.5 w-3.5" /></button>
                        {canDelete && (
                          <button data-testid={`inventory-delete-${i.id.slice(0, 8)}`} onClick={() => remove(i.id)} className="h-7 w-7 rounded-full border border-red-200 flex items-center justify-center text-red-500 hover:bg-red-50" aria-label="Delete item"><Trash2 className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {txnItem && (
        <Modal testid="stock-txn-modal" onClose={() => setTxnItem(null)}
          title={`Stock Update — ${txnItem.item}`}
          subtitle={`Current stock: ${txnItem.stock} ${txnItem.unit || "pcs"} · Reorder at ${txnItem.reorder_level}`}>
          <form onSubmit={submitTxn} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Transaction *</label>
                <select data-testid="txn-type" value={txnForm.type} onChange={(e) => setTxnForm({ ...txnForm, type: e.target.value })} className={`mt-1 ${inputCls}`}>
                  <option value="in">Stock In (purchase/return)</option>
                  <option value="out">Stock Out</option>
                  <option value="consume">Consumption (used in treatment)</option>
                  <option value="transfer">Transfer to another clinic</option>
                  <option value="adjust">Adjustment (correction)</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Quantity *</label>
                <input required type="number" min="1" inputMode="numeric" data-testid="txn-qty" value={txnForm.qty} onChange={(e) => setTxnForm({ ...txnForm, qty: e.target.value })} className={`mt-1 ${inputCls}`} />
              </div>
            </div>
            {txnForm.type === "adjust" && (
              <div>
                <label className={labelCls}>Direction *</label>
                <select data-testid="txn-direction" value={txnForm.direction || "in"} onChange={(e) => setTxnForm({ ...txnForm, direction: e.target.value })} className={`mt-1 ${inputCls}`}>
                  <option value="in">Increase stock</option>
                  <option value="out">Decrease stock</option>
                </select>
              </div>
            )}
            <div>
              <label className={labelCls}>Notes</label>
              <input data-testid="txn-notes" value={txnForm.notes} onChange={(e) => setTxnForm({ ...txnForm, notes: e.target.value })} className={`mt-1 ${inputCls}`} placeholder="e.g. Used in root canal, PO-1002 received…" />
            </div>
            <p className="text-[11px] text-slate-400">Every transaction is logged with date, time and your login — and flows into the daily report.</p>
            <button type="submit" disabled={txnBusy} data-testid="txn-submit"
              className="w-full rounded-full bg-teal-brand px-5 py-3 text-sm font-bold text-white hover:bg-pine transition-colors disabled:opacity-50">
              {txnBusy ? "Updating…" : "Update Stock"}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
